// src/App.tsx
import { logger } from './utils/logger';

import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import {
  IonApp,
  IonIcon,
  IonLabel,
  IonRouterOutlet,
  IonTabBar,
  IonTabButton,
  IonTabs,
  IonBadge,
  IonSplitPane,
  IonMenu,
  IonContent,
  setupIonicReact
} from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { compass, chatbubbles, personCircle, calendar, gameController } from 'ionicons/icons';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { User } from '@supabase/supabase-js';
import { Purchases, LOG_LEVEL } from '@revenuecat/purchases-capacitor';

// Auth Pages
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import AuthCallback from './pages/auth/AuthCallback';
import ResetPassword from './pages/auth/ResetPassword';
import VerifyDeletion from './pages/VerifyDeletion';

// Main Tab Pages
import Tab1 from './pages/Tab1';
import Tab2 from './pages/Tab2';
import Tab3 from './pages/Tab3';
import SessionsList from './pages/sessions/SessionsList';
import GameHub from './pages/GameHub';

// Profile Pages
import EditProfile from './pages/profile/EditProfile';
import ManageGames from './pages/profile/ManageGames';
import ManageAvailability from './pages/profile/ManageAvailability';
import AccountSettings from './pages/profile/AccountSettings';
import BlockedUsers from './pages/BlockedUsers';
import AdminReports from './pages/AdminReports';
import { SubscriptionSettings } from './pages/SubscriptionSettings';

// Session Pages
import CreateSession from './pages/sessions/CreateSession';
import SessionDetail from './pages/sessions/SessionDetail';

// Game Pages
import OldschoolDungeonEscape from './components/games/OldschoolDungeonEscape';
import { PipsWordPuzzleMenu } from './components/games/pipsWordPuzzle/PipsWordPuzzleMenu';
import { PipsWordPuzzle } from './components/games/pipsWordPuzzle/PipsWordPuzzle';
import { PipsWordPuzzleArchive } from './components/games/pipsWordPuzzle/PipsWordPuzzleArchive';
import { PipsWordPuzzleStats } from './components/games/pipsWordPuzzle/PipsWordPuzzleStats';
import PipsGuildQuestHub from './components/games/pipsGuildQuest/PipsGuildQuestHub';
import QuestView from './components/games/pipsGuildQuest/QuestView';
import QuestCreation from './components/games/pipsGuildQuest/QuestCreation';
import PipsCreatureKeeper from './pages/PipsCreatureKeeper';
import DiceMatch from './components/games/diceMatch/DiceMatch';
import DiceMatchMenu from './components/games/diceMatch/DiceMatchMenu';
import DiceMatchUpgrades from './components/games/diceMatch/DiceMatchUpgrades';
import DiceMatchTrinkets from './components/games/diceMatch/DiceMatchTrinkets';
import Crossroads from './components/games/crossroads/Crossroads';
import CrossroadsMenu from './components/games/crossroads/CrossroadsMenu';
import CrossroadsMultiplayerHub from './components/games/crossroads/CrossroadsMultiplayerHub';
import ErrorBoundary from './components/ErrorBoundary';

// Services
import { supabase } from './services/supabase';
import { pushNotificationService } from './services/pushNotificationService';
import { getTotalUnreadCount, subscribeToUnreadChanges } from './services/unreadService';
import { themeService } from './services/themeService';
import { soundService } from './services/soundService';

// Context Providers
import { ChatsDataProvider } from './contexts/ChatsDataContext';
import { SessionsDataProvider } from './contexts/SessionsDataContext';
import { ProfileProvider } from './contexts/ProfileContext';
import { MatchesDataProvider } from './contexts/MatchesDataContext';
import { AccessibilityProvider } from './contexts/AccessibilityContext';

// Components
import { CookieConsent } from './components/CookieConsent';
import { Sidebar } from './components/layout/Sidebar';

// Onboarding
import { OnboardingTutorial, useOnboarding, defaultOnboardingSteps } from './components/OnboardingTutorial';

// Core CSS
import './theme/variables.css';
import '@ionic/react/css/core.css';
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';
import '@ionic/react/css/palettes/dark.system.css';

// Custom CSS - ORDER MATTERS!
import './theme/global-text-fixes.css';
import './safe-area-fix.css';
import './theme/tab-bar-fixes.css';
import './theme/segment-title-bar-styling.css';
import './theme/highlight-text.css'; // ✅ ADDED - Must be last to override
import './styles/accessibility.css'; // ✅ Accessibility features
import './styles/rainbow-theme.css'; // 🌈 Rainbow Pride theme
import './styles/layout.css'; // Responsive desktop layout

setupIonicReact();

// Route change theme refresh component
const RouteChangeThemeRefresh: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    themeService.forceRefresh();
  }, [location.pathname]);

  return null;
};

// Tabs wrapper component with navigation support for tutorial
const TabsWrapper: React.FC<{
  hasCompletedOnboarding: boolean;
  markOnboardingComplete: () => void;
  totalUnreadCount: number;
}> = ({ hasCompletedOnboarding, markOnboardingComplete, totalUnreadCount }) => {
  const history = useHistory();

  const handleTabChange = (tab: string) => {
    const tabMap: { [key: string]: string } = {
      tab1: '/tabs/discover',
      tab2: '/tabs/chats',
      tab3: '/tabs/profile',
      tab4: '/tabs/games',
    };
    const route = tabMap[tab];
    if (route) history.push(route);
  };

  return (
    <>
      {!hasCompletedOnboarding && (
        <OnboardingTutorial
          steps={defaultOnboardingSteps}
          onComplete={markOnboardingComplete}
          onSkip={markOnboardingComplete}
          onTabChange={handleTabChange}
        />
      )}

      {/*
        IonSplitPane + IonMenu provide the desktop sidebar.
        when="md" keeps the sidebar permanently visible at ≥768px.
        On mobile the IonMenu is hidden and IonTabBar takes over.
        Ionic adds .split-pane-visible when the sidebar is shown;
        layout.css uses that class to hide the tab bar on desktop.
      */}
      <IonSplitPane contentId="main-tabs" when="md">
        <IonMenu
          contentId="main-tabs"
          menuId="sidebar-menu"
          className="sidebar-menu"
          type="push"
          swipeGesture={false}
        >
          <IonContent>
            <Sidebar unreadCount={totalUnreadCount} />
          </IonContent>
        </IonMenu>

      <div id="main-tabs" className="ion-page">
      <IonTabs>
        <IonRouterOutlet>
          <Switch>
            <Route exact path="/tabs/discover">
              <Tab1 />
            </Route>
            <Route exact path="/tabs/chats">
              <Tab2 />
            </Route>
            <Route exact path="/tabs/sessions">
              <SessionsList />
            </Route>
            <Route exact path="/tabs/sessions/create">
              <CreateSession />
            </Route>
            <Route exact path="/tabs/sessions/:id">
              <SessionDetail />
            </Route>
            <Route exact path="/tabs/games">
              <GameHub />
            </Route>
            <Route path="/tabs/games/pips-creature-keeper">
              <ErrorBoundary gameName="Pip's Creature Keeper" resetPath="/tabs/games">
                <PipsCreatureKeeper />
              </ErrorBoundary>
            </Route>
            <Route exact path="/tabs/profile">
              <Tab3 />
            </Route>
            <Route exact path="/tabs">
              <Redirect to="/tabs/discover" />
            </Route>
          </Switch>
        </IonRouterOutlet>

        <IonTabBar slot="bottom">
          <IonTabButton tab="discover" href="/tabs/discover">
            <IonIcon aria-hidden="true" icon={compass} />
            <IonLabel>Discover</IonLabel>
          </IonTabButton>

          <IonTabButton tab="chats" href="/tabs/chats">
            <IonIcon aria-hidden="true" icon={chatbubbles} />
            <IonLabel>Chats</IonLabel>
            {totalUnreadCount > 0 && (
              <IonBadge color="danger">{totalUnreadCount}</IonBadge>
            )}
          </IonTabButton>

          <IonTabButton tab="sessions" href="/tabs/sessions">
            <IonIcon aria-hidden="true" icon={calendar} />
            <IonLabel>Sessions</IonLabel>
          </IonTabButton>

          <IonTabButton tab="games" href="/tabs/games">
            <IonIcon aria-hidden="true" icon={gameController} />
            <IonLabel>Games</IonLabel>
          </IonTabButton>

          <IonTabButton tab="profile" href="/tabs/profile">
            <IonIcon aria-hidden="true" icon={personCircle} />
            <IonLabel>Profile</IonLabel>
          </IonTabButton>
        </IonTabBar>
      </IonTabs>
      </div>

      </IonSplitPane>
    </>
  );
};

const App: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const { hasCompletedOnboarding, markOnboardingComplete } = useOnboarding();

  useEffect(() => {
    // Initialize theme service immediately
    themeService.initialize();

    // Load global UI sounds
    soundService.loadSounds({
      'button-click': '/assets/sounds/ui/click.mp3',
      'success': '/assets/sounds/ui/success.mp3',
      'error': '/assets/sounds/ui/error.mp3',
      'notification': '/assets/sounds/ui/notification.mp3',
    });

    // Track if RevenueCat is configured
    let isRevenueCatConfigured = false;

    // Initialize RevenueCat
    const initializeRevenueCat = async () => {
      try {
        const platform = Capacitor.getPlatform();

        if (platform === 'ios') {
          const apiKey = import.meta.env.VITE_REVENUECAT_PUBLIC_IOS_KEY;

          if (apiKey && !apiKey.includes('your-')) {
            await Purchases.configure({
              apiKey,
              appUserID: undefined, // Will be set after authentication
            });

            // Enable debug logs in development
            if (import.meta.env.DEV) {
              await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
            }

            isRevenueCatConfigured = true;
            logger.log('[RevenueCat] Initialized successfully');
          } else {
            logger.warn('[RevenueCat] iOS API key not configured');
          }
        } else if (platform === 'android') {
          const apiKey = import.meta.env.VITE_REVENUECAT_PUBLIC_ANDROID_KEY;

          if (apiKey && !apiKey.includes('your-')) {
            await Purchases.configure({
              apiKey,
              appUserID: undefined, // Will be set after authentication
            });

            // Enable debug logs in development
            if (import.meta.env.DEV) {
              await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });
            }

            isRevenueCatConfigured = true;
            logger.log('[RevenueCat] Initialized successfully');
          } else {
            logger.warn('[RevenueCat] Android API key not configured');
          }
        }
      } catch (error) {
        logger.error('[RevenueCat] Failed to initialize:', error);
      }
    };

    initializeRevenueCat();

    // Check current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);

      if (session?.user) {
        // Set RevenueCat user ID
        if (isRevenueCatConfigured) {
          Purchases.logIn({ appUserID: session.user.id }).catch(logger.error);
        }
        pushNotificationService.register();
        loadUnreadCount();
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);

      if (session?.user) {
        // Set RevenueCat user ID
        if (isRevenueCatConfigured) {
          Purchases.logIn({ appUserID: session.user.id }).catch(logger.error);
        }
        pushNotificationService.register();
        loadUnreadCount();

        // Redirect from login page after authentication
        if (window.location.pathname === '/login') {
          window.location.href = '/tabs/discover';
        }
      } else {
        // Log out of RevenueCat
        if (isRevenueCatConfigured) {
          Purchases.logOut().catch(logger.error);
        }
        setTotalUnreadCount(0);
      }
    });

    // Handle deep links for mobile app
    CapApp.addListener('appUrlOpen', (event: any) => {
      if (event.url.includes('auth/callback')) {
        const url = event.url.replace('com.dicecat.app://', '');
        const hashIndex = url.indexOf('#');

        if (hashIndex !== -1) {
          const hash = url.substring(hashIndex);
          window.location.href = `/auth/callback${hash}`;
        } else {
          window.location.href = '/auth/callback';
        }
      }
    });

    return () => {
      subscription.unsubscribe();
      CapApp.removeAllListeners();
    };
  }, []);

  // Subscribe to unread count changes
  useEffect(() => {
    if (!user) return;

    const unsubscribe = subscribeToUnreadChanges(() => {
      loadUnreadCount();
    });

    return () => {
      unsubscribe();
    };
  }, [user]);

  // Force refresh listener for immediate badge updates
  useEffect(() => {
    if (!user) return;

    const handleForceRefresh = () => {
      loadUnreadCount();
    };

    window.addEventListener('force-unread-refresh', handleForceRefresh);

    return () => {
      window.removeEventListener('force-unread-refresh', handleForceRefresh);
    };
  }, [user]);

  const loadUnreadCount = async () => {
    try {
      const result = await getTotalUnreadCount();
      if (result.success && result.counts) {
        setTotalUnreadCount(result.counts.total);
      }
    } catch (error) {
      // Silently fail on network errors - don't disrupt user experience
      logger.warn('[Unread Count] Failed to load:', error);
    }
  };

  if (loading) {
    return (
      <IonApp>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh'
        }}>
          Loading...
        </div>
      </IonApp>
    );
  }

  return (
    <IonApp>
      <AccessibilityProvider>
        <IonReactRouter>
          {/* Skip to main content link for keyboard navigation - WCAG 2.4.1 Level A */}
          <a href="#main-content" className="skip-link">
            Skip to main content
          </a>
          <RouteChangeThemeRefresh />
        <IonRouterOutlet id="main-content">
          {/* Auth Routes */}
          <Route exact path="/login">
            <Login />
          </Route>
          <Route exact path="/signup">
            <Signup />
          </Route>
          <Route exact path="/auth/callback">
            <AuthCallback />
          </Route>
          <Route exact path="/auth/reset-password">
            <ResetPassword />
          </Route>
          <Route exact path="/verify-deletion">
            <VerifyDeletion />
          </Route>

          {/* Pip's Word Puzzle Game Routes - Outside Tabs */}
          <Route exact path="/games/pips-word-puzzle/daily">
            {user ? (
              <ErrorBoundary gameName="Pip's Word Puzzle" resetPath="/games/pips-word-puzzle">
                <PipsWordPuzzle />
              </ErrorBoundary>
            ) : <Redirect to="/login" />}
          </Route>
          <Route exact path="/games/pips-word-puzzle/play">
            {user ? (
              <ErrorBoundary gameName="Pip's Word Puzzle" resetPath="/games/pips-word-puzzle">
                <PipsWordPuzzle />
              </ErrorBoundary>
            ) : <Redirect to="/login" />}
          </Route>
          <Route exact path="/games/pips-word-puzzle/archive">
            {user ? (
              <ErrorBoundary gameName="Pip's Word Puzzle Archive" resetPath="/games/pips-word-puzzle">
                <PipsWordPuzzleArchive />
              </ErrorBoundary>
            ) : <Redirect to="/login" />}
          </Route>
          <Route exact path="/games/pips-word-puzzle/stats">
            {user ? (
              <ErrorBoundary gameName="Pip's Word Puzzle Stats" resetPath="/games/pips-word-puzzle">
                <PipsWordPuzzleStats />
              </ErrorBoundary>
            ) : <Redirect to="/login" />}
          </Route>
          <Route exact path="/games/pips-word-puzzle">
            {user ? <PipsWordPuzzleMenu /> : <Redirect to="/login" />}
          </Route>

          {/* Other Game Routes - Outside Tabs */}
          <Route exact path="/games/oldschool-dungeon-escape">
            {user ? (
              <ErrorBoundary gameName="Oldschool Dungeon Escape" resetPath="/tabs/games">
                <OldschoolDungeonEscape />
              </ErrorBoundary>
            ) : <Redirect to="/login" />}
          </Route>

          {/* Dice Match Game Route */}
          <Route exact path="/games/dice-match">
            {user ? (
              <ErrorBoundary gameName="Dice Match" resetPath="/games/dice-match/menu">
                <DiceMatch />
              </ErrorBoundary>
            ) : <Redirect to="/login" />}
          </Route>
          {/* Crossroads TCG Routes - wrapped with ChatsDataProvider for challenge functionality */}
          <Route exact path="/games/crossroads">
            {user ? (
              <ChatsDataProvider>
                <CrossroadsMenu />
              </ChatsDataProvider>
            ) : <Redirect to="/login" />}
          </Route>
          <Route exact path="/games/crossroads/singleplayer">
            {user ? (
              <ErrorBoundary gameName="Crossroads TCG" resetPath="/games/crossroads">
                <ChatsDataProvider>
                  <Crossroads />
                </ChatsDataProvider>
              </ErrorBoundary>
            ) : <Redirect to="/login" />}
          </Route>
          <Route exact path="/games/crossroads/multiplayer">
            {user ? (
              <ErrorBoundary gameName="Crossroads TCG Multiplayer" resetPath="/games/crossroads">
                <ChatsDataProvider>
                  <CrossroadsMultiplayerHub onBack={() => window.history.back()} />
                </ChatsDataProvider>
              </ErrorBoundary>
            ) : <Redirect to="/login" />}
          </Route>

          {/* Dice Match Routes */}
          <Route exact path="/games/dice-match/menu">
            {user ? <DiceMatchMenu /> : <Redirect to="/login" />}
          </Route>
          <Route exact path="/games/dice-match/upgrades">
            {user ? <DiceMatchUpgrades /> : <Redirect to="/login" />}
          </Route>
          <Route exact path="/games/dice-match/trinkets">
            {user ? <DiceMatchTrinkets /> : <Redirect to="/login" />}
          </Route>
          <Route exact path="/games/dice-match">
            {user ? <DiceMatch /> : <Redirect to="/login" />}
          </Route>

          {/* Pip's Guild Quest Routes - Outside Tabs */}
          <Route exact path="/games/pips-guild-quest">
            {user ? <PipsGuildQuestHub /> : <Redirect to="/login" />}
          </Route>
          <Route exact path="/games/guild-quest/create/:templateId">
            {user ? (
              <ErrorBoundary gameName="Pip's Guild Quest Creation" resetPath="/games/pips-guild-quest">
                <QuestCreation />
              </ErrorBoundary>
            ) : <Redirect to="/login" />}
          </Route>
          <Route exact path="/games/guild-quest/play/:questId">
            {user ? (
              <ErrorBoundary gameName="Pip's Guild Quest" resetPath="/games/pips-guild-quest">
                <QuestView />
              </ErrorBoundary>
            ) : <Redirect to="/login" />}
          </Route>

          {/* Session Routes - Outside Tabs */}
          <Route exact path="/sessions/create">
            {user ? <CreateSession /> : <Redirect to="/login" />}
          </Route>
          <Route exact path="/sessions/:id">
            {user ? <SessionDetail /> : <Redirect to="/login" />}
          </Route>

          {/* Profile Routes - Outside Tabs */}
          <Route exact path="/profile/edit">
            {user ? <EditProfile /> : <Redirect to="/login" />}
          </Route>
          <Route exact path="/profile/games">
            {user ? <ManageGames /> : <Redirect to="/login" />}
          </Route>
          <Route exact path="/profile/availability">
            {user ? <ManageAvailability /> : <Redirect to="/login" />}
          </Route>
          <Route exact path="/profile/settings">
            {user ? <AccountSettings /> : <Redirect to="/login" />}
          </Route>
          <Route exact path="/blocked-users">
            {user ? <BlockedUsers /> : <Redirect to="/login" />}
          </Route>
          <Route exact path="/admin/reports">
            {user ? <AdminReports /> : <Redirect to="/login" />}
          </Route>
          <Route exact path="/settings/subscription">
            {user ? <SubscriptionSettings /> : <Redirect to="/login" />}
          </Route>

          {/* Main App with Tabs */}
          <Route path="/tabs">
            {user ? (
              <ProfileProvider>
                <ChatsDataProvider>
                  <SessionsDataProvider>
                    <MatchesDataProvider>
                      <TabsWrapper
                        hasCompletedOnboarding={hasCompletedOnboarding}
                        markOnboardingComplete={markOnboardingComplete}
                        totalUnreadCount={totalUnreadCount}
                      />
                    </MatchesDataProvider>
                  </SessionsDataProvider>
                </ChatsDataProvider>
              </ProfileProvider>
            ) : (
              <Redirect to="/login" />
            )}
          </Route>

          {/* Root Redirect */}
          <Route exact path="/">
            <Redirect to={user ? "/tabs/discover" : "/login"} />
          </Route>
        </IonRouterOutlet>
        </IonReactRouter>
      </AccessibilityProvider>

      {/* Cookie Consent Banner - GDPR Compliance (Web only, not shown on mobile platforms) */}
      {Capacitor.getPlatform() === 'web' && <CookieConsent />}
    </IonApp>
  );
};

export default App;